import { Box, Flex, Icon, Text } from '@/ui';
import type { BoxProps, FlexProps } from '@/ui';

// ── SidebarSection ─────────────────────────────────────────────────────────────

type SidebarSectionProps = {
  label: string;
  scope?: string;
  children: React.ReactNode;
};

export const SidebarSection = ({
  label,
  scope,
  children,
}: SidebarSectionProps) => (
  <Box mb={6}>
    <Flex align="baseline" justify="space-between" mb={2}>
      <Text
        color="ink.soft"
        fontSize="ah-status"
        fontWeight={700}
        letterSpacing="0.09em"
        ml={0.5}
        textTransform="uppercase"
      >
        {label}
      </Text>
      {scope && (
        <Text color="ink.muted" fontSize="ah-status" fontWeight={400}>
          {scope}
        </Text>
      )}
    </Flex>
    {children}
  </Box>
);

// ── SidebarCard ────────────────────────────────────────────────────────────────

type SidebarCardProps = BoxProps & {
  children: React.ReactNode;
};

export const SidebarCard = ({ children, ...rest }: SidebarCardProps) => (
  <Box layerStyle="ahCard" overflow="hidden" py={1} {...rest}>
    {children}
  </Box>
);

// ── SidebarRow ─────────────────────────────────────────────────────────────────

type SidebarRowProps = FlexProps & {
  children: React.ReactNode;
};

export const SidebarRow = ({ children, ...rest }: SidebarRowProps) => (
  <Flex
    _last={{ borderBottom: 'none' }}
    align="center"
    borderBottom="1px solid"
    borderColor="line.soft"
    gap={3}
    justify="space-between"
    px={3.5}
    py="2"
    {...rest}
  >
    {children}
  </Flex>
);

// ── SidebarRowLabel ────────────────────────────────────────────────────────────

type SidebarRowLabelProps = {
  label: string;
  sublabel?: string;
};

export const SidebarRowLabel = ({ label, sublabel }: SidebarRowLabelProps) => (
  <Box minW={0}>
    <Text textStyle="ahRow">{label}</Text>
    {sublabel && (
      <Text color="ink.muted" fontSize="ah-status" mt={0.5}>
        {sublabel}
      </Text>
    )}
  </Box>
);

// ── RadioRow ───────────────────────────────────────────────────────────────────
// variant="dot"   — filled brand circle (default); used for mutually-exclusive list options
// variant="check" — magicPurple checkmark; used for single-option toggles with a label

export type RadioRowProps = Pick<FlexProps, 'onMouseEnter' | 'onMouseLeave'> & {
  isActive: boolean;
  label: string;
  sublabel?: string;
  variant?: 'dot' | 'check';
  onClick: () => void;
};

export const RadioRow = ({
  isActive,
  label,
  sublabel,
  variant = 'dot',
  onClick,
  ...hoverProps
}: RadioRowProps) => (
  <SidebarRow
    _hover={{ bg: variant === 'check' ? 'magicPurple.25' : 'rgba(0,0,0,0.02)' }}
    cursor="pointer"
    onClick={onClick}
    {...hoverProps}
  >
    {variant === 'dot' ? (
      <Flex align="center" gap={3} minW={0}>
        <Box
          bg={isActive ? 'brand.500' : 'transparent'}
          border="2px solid"
          borderColor={isActive ? 'brand.500' : 'line.DEFAULT'}
          borderRadius="full"
          flexShrink={0}
          h="16px"
          transition="background 0.14s, border-color 0.14s"
          w="16px"
        />
        <SidebarRowLabel label={label} sublabel={sublabel} />
      </Flex>
    ) : (
      <>
        <SidebarRowLabel label={label} sublabel={sublabel} />
        {isActive && (
          <Icon
            color="magicPurple.500"
            flexShrink={0}
            icon="Checkmark"
            size={4}
          />
        )}
      </>
    )}
  </SidebarRow>
);

// ── SidebarSubLabel ────────────────────────────────────────────────────────────
// Uppercase group heading used *inside* a SidebarSection to label sub-groups.
// Smaller and lighter than SidebarSection's label to create visual hierarchy.
// `scope` adds the same right-aligned caption SidebarSection uses, for a
// sub-group whose reach differs from the section around it (e.g. a whole-schedule
// control inside a per-view section).

type SidebarSubLabelProps = {
  children: React.ReactNode;
  mt?: number;
  scope?: string;
};

export const SidebarSubLabel = ({
  children,
  mt = 0,
  scope,
}: SidebarSubLabelProps) => {
  const label = (
    <Text
      color="ink.muted"
      fontSize="ah-status"
      fontWeight={600}
      letterSpacing="0.06em"
      mb={scope ? 0 : 1.5}
      mt={scope ? 0 : mt}
      px={0.5}
      textTransform="uppercase"
    >
      {children}
    </Text>
  );

  if (!scope) {
    return label;
  }

  return (
    <Flex align="baseline" justify="space-between" mb={1.5} mt={mt}>
      {label}
      <Text color="ink.muted" fontSize="ah-status" fontWeight={400}>
        {scope}
      </Text>
    </Flex>
  );
};

// ── SidebarIconChip ────────────────────────────────────────────────────────────
// variant="neutral" — gray chip with ink.soft icon (default; used in row labels, nav items)
// variant="purple"  — magicPurple tinted chip (used in icon CTAs, highlighted nav states)

type SidebarIconChipProps = BoxProps & {
  children: React.ReactNode;
  size?: string | number;
  variant?: 'neutral' | 'purple';
};

export const SidebarIconChip = ({
  children,
  size = '28px',
  variant = 'neutral',
  ...rest
}: SidebarIconChipProps) => (
  <Box
    alignItems="center"
    bg={variant === 'neutral' ? 'rgba(0,0,0,0.05)' : undefined}
    borderRadius="ah-md"
    display="inline-flex"
    flexShrink={0}
    h={size}
    justifyContent="center"
    layerStyle={variant === 'purple' ? 'ahIconChip' : undefined}
    w={size}
    {...rest}
  >
    {children}
  </Box>
);
